import { cloneElement } from "react";
import { iconForPage } from "./nav";
import { readableOn } from "./postBackgrounds";

// A premium, brand-aware loading state shared across the app's full-screen and
// embedded loading moments: an ambient brand glow, one slow halo, a single
// gradient arc sweeping a faint track, and a brand-filled disc carrying the
// icon of the page being loaded — all tinted with the community's primary
// color so it feels native to each space.
//
// Props:
//   label    – text under the spinner (default "Loading…")
//   primary  – brand color used for the ring + core (hex)
//   embedded – render inline (no full-height background) instead of full-screen
//   logo     – optional image URL shown inside the spinner instead of the core
//   page     – the page being loaded ("Members", "memberchat", "Loading map…").
//              Names the loader AND picks the icon shown inside the ring.
//   icon     – an explicit icon element, when the page has no entry in nav's
//              PAGE_ICON (a one-off screen)
export default function PageLoader({
  label = "Loading…",
  primary = "#7C3AED",
  embedded = false,
  logo,
  page,
  icon,
}) {
  // The page name headlines the loader. Prefer an explicit `page`; otherwise
  // pull it out of a "Loading <page>…" label (e.g. "Loading Members…" → "Members").
  const derived = page || (label || "").match(/^\s*loading\s+(.+?)[.…\s]*$/i)?.[1] || "";
  const pageName = derived ? derived.charAt(0).toUpperCase() + derived.slice(1) : "";
  // The centre of the ring says WHICH page is coming. The page's own nav icon
  // is the honest answer — the member is looking at the same mark they clicked
  // — so it outranks the community logo, which is identical on every screen and
  // therefore tells them nothing about what they are waiting for. The logo
  // stays as the fallback for screens outside the nav (login, a group landing).
  const pageIcon = icon || iconForPage(derived);

  return (
    <div
      className={`${embedded ? "py-24" : "min-h-screen"} relative flex flex-col items-center justify-center gap-6 overflow-hidden animate-fade-in`}
      style={embedded ? undefined : { background: `radial-gradient(120% 120% at 50% 32%, ${primary}12 0%, #f7f8fa 52%, #eceef2 100%)` }}
    >
      <style>{`
        @keyframes pl-spin { to { transform: rotate(360deg); } }
        @keyframes pl-dot { 0%,100% { opacity: .35; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-2px); } }
        @keyframes pl-halo { 0% { transform: scale(1); opacity: .32; } 100% { transform: scale(1.45); opacity: 0; } }
        /* The disc breathes far more gently than the tailwind \`breathe\` used by
           the small brand core: at 78px that keyframe's 0.82 scale + opacity dip
           is a throb, not a breath. */
        @keyframes pl-breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.035); } }
      `}</style>

      {/* Emblem — one ring, one halo, one solid centre.
          The old build stacked five circles inside 96px (two sonar pulses, a
          faint track, a counter-rotating arc, the arc itself) around a small
          white chip, and at that size they read as concentric hairlines — busy
          and thin rather than considered. What is left is the part that carries
          meaning: a single sweeping arc for progress, one slow halo for life,
          and a brand-filled disc big enough for the page's icon to be the thing
          you actually look at. */}
      <div className="relative flex h-[116px] w-[116px] items-center justify-center">
        <div className="absolute -inset-4 rounded-full blur-[28px]" style={{ background: `radial-gradient(circle, ${primary}3d 0%, transparent 68%)` }} aria-hidden="true" />
        {/* Halo — expands OUT of the ring rather than inside it, so it never
            crosses the disc and adds a circle to count. */}
        <span className="absolute inset-0 rounded-full" style={{ border: `1.5px solid ${primary}73`, animation: "pl-halo 2.8s ease-out infinite" }} aria-hidden="true" />

        {/* Full track, and the arc that sweeps it. The arc fades in from
            transparent instead of starting at full strength, which is what
            makes it read as a sweep rather than as a rotating pie slice. */}
        <div className="absolute inset-0 rounded-full" style={{ border: `2.5px solid ${primary}1a` }} aria-hidden="true" />
        <div
          className="absolute inset-0 rounded-full"
          style={{
            animation: "pl-spin 1.1s linear infinite",
            background: `conic-gradient(from 0deg, transparent 0deg, ${primary}1f 120deg, ${primary} 350deg, transparent 360deg)`,
            WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2.5px))",
            mask: "radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2.5px))",
          }}
          aria-hidden="true"
        />

        {/* Centre — the page's icon on brand, else the logo or a brand core on
            white. Brand fill is for the icon only: a logo carries its own
            colours and a wordmark on a coloured disc is where this stops
            looking designed. */}
        <div
          className="relative flex h-[78px] w-[78px] items-center justify-center rounded-full"
          style={
            pageIcon
              ? {
                  // A white sheen off the top-left, not a second hand-picked
                  // colour: it gives the disc its curve from whatever hue the
                  // community's brand happens to be.
                  background: `radial-gradient(circle at 32% 24%, rgba(255,255,255,0.30), rgba(255,255,255,0) 58%), ${primary}`,
                  boxShadow: `0 16px 30px -14px ${primary}b3, inset 0 1px 0 rgba(255,255,255,0.3)`,
                  animation: "pl-breathe 3.2s ease-in-out infinite",
                }
              : {
                  background: "linear-gradient(180deg, #ffffff 0%, #f6f8fa 100%)",
                  boxShadow: `0 16px 30px -16px rgba(15,23,42,0.45), 0 0 0 1px ${primary}1f`,
                  animation: "pl-breathe 3.2s ease-in-out infinite",
                }
          }
        >
          {pageIcon ? (
            // Ink is computed, not assumed white — a pale brand (amber, lime)
            // would otherwise erase the icon the loader exists to show.
            <span className="flex items-center justify-center" style={{ color: readableOn(primary) }}>
              {cloneElement(pageIcon, { className: "h-[33px] w-[33px]" })}
            </span>
          ) : logo ? (
            <img src={logo} alt="" className="h-[58%] w-[58%] rounded-xl object-contain" />
          ) : (
            <div className="h-[38%] w-[38%] rounded-full animate-breathe" style={{ background: `radial-gradient(circle at 32% 28%, ${primary}, ${primary}cc 70%)`, boxShadow: `0 0 12px ${primary}55` }} />
          )}
        </div>
      </div>

      {/* Page name + a "Loading" line with animated brand dots. */}
      <div className="flex flex-col items-center gap-1.5 text-center">
        {pageName && <h2 className="text-xl font-extrabold tracking-tight text-gray-900 sm:text-[22px]">{pageName}</h2>}
        <div className="flex items-center gap-1.5">
          <span className="text-[11.5px] font-bold uppercase tracking-[0.14em]" style={{ color: primary }}>Loading</span>
          <span className="flex items-end gap-0.5 pb-0.5" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span key={i} className="h-[3px] w-[3px] rounded-full" style={{ backgroundColor: primary, animation: "pl-dot 1s ease-in-out infinite", animationDelay: `${i * 0.15}s` }} />
            ))}
          </span>
        </div>
      </div>
    </div>
  );
}

// A compact, premium inline spinner that mirrors the page loader's main ring: a
// conic-gradient arc led by a glowing comet dot. It inherits `currentColor`, so
// it renders white inside a primary button and grey/brand when used standalone.
// Pass `size` in px (default 16); positioning/color go through `className`.
export function Spinner({ size = 16, className = "" }) {
  const stroke = Math.max(2, Math.round(size / 8));
  return (
    <span
      role="status"
      aria-label="Loading"
      className={`relative inline-block shrink-0 animate-spin ${className}`}
      style={{ width: size, height: size, animationDuration: "0.7s" }}
    >
      <span
        className="absolute inset-0 rounded-full"
        style={{
          background: "conic-gradient(from 90deg, transparent 0deg, currentColor 300deg)",
          WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${stroke}px), #000 calc(100% - ${stroke}px))`,
          mask: `radial-gradient(farthest-side, transparent calc(100% - ${stroke}px), #000 calc(100% - ${stroke}px))`,
        }}
      />
      <span
        className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: stroke,
          height: stroke,
          background: "currentColor",
          boxShadow: `0 0 ${Math.max(3, stroke * 2)}px currentColor`,
        }}
      />
    </span>
  );
}

// A compact "spinner + label" row for spaces the full PageLoader would dwarf —
// dropdown panels and lesson tab panes. Pass `primary` to tint it with the
// brand color; without it the spinner inherits the surrounding text color.
// Defaults to left-aligned; pass `className="justify-center"` to center it.
export function InlineLoader({ label = "Loading…", primary, size = 14, className = "" }) {
  return (
    <p className={`flex items-center gap-2 text-sm text-gray-400 ${className}`}>
      <span className="inline-flex" style={primary ? { color: primary } : undefined}>
        <Spinner size={size} />
      </span>
      {label}
    </p>
  );
}

// A single shimmering placeholder bar. Uses a moving gradient (animate-shimmer)
// rather than a flat pulse so it reads as actively loading.
function Bar({ className = "" }) {
  return (
    <div
      className={`rounded-md bg-gray-200 animate-shimmer ${className}`}
      style={{
        backgroundImage:
          "linear-gradient(90deg, #e9eaee 0%, #f4f5f7 50%, #e9eaee 100%)",
        backgroundSize: "200% 100%",
      }}
    />
  );
}

// Skeleton stand-in for a feed post card, shown while posts load. Mirrors the
// real PostCard layout (avatar + name, body lines, media block, action row) so
// the transition to loaded content is smooth and non-jarring.
export function PostCardSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-surface p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <Bar className="h-11 w-11 !rounded-full" />
        <div className="flex-1 space-y-2">
          <Bar className="h-3.5 w-1/3" />
          <Bar className="h-3 w-1/4" />
        </div>
      </div>
      <div className="mt-4 space-y-2.5">
        <Bar className="h-3 w-full" />
        <Bar className="h-3 w-11/12" />
        <Bar className="h-3 w-3/4" />
      </div>
      <Bar className="mt-4 h-44 w-full !rounded-xl" />
      <div className="mt-4 flex gap-6 border-t border-gray-100 pt-4">
        <Bar className="h-3.5 w-16" />
        <Bar className="h-3.5 w-16" />
        <Bar className="h-3.5 w-16" />
      </div>
    </div>
  );
}

// A short stack of post skeletons for the loading feed.
export function FeedSkeleton({ count = 3 }) {
  return (
    <div className="space-y-4 animate-fade-in">
      {Array.from({ length: count }).map((_, i) => (
        <PostCardSkeleton key={i} />
      ))}
    </div>
  );
}
